import { useId } from 'react';
import { faCheck, faSparkles } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';

import { Button, buttonVariants } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { MutedText, SmallText } from '@/components/ui/typography';
import { BUTTON_ICON_OPTIONS } from '@/lib/blocks/button-links';
import { cn } from '@/lib/utils';

export function ButtonEditorSettingToggle({
  title,
  description,
  checked,
  onCheckedChange,
}: {
  title: string;
  description?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex min-w-0 flex-col">
        <SmallText className="font-medium leading-5">{title}</SmallText>
        {description ? <MutedText className="leading-5">{description}</MutedText> : null}
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} aria-label={title} />
    </div>
  );
}

export function ButtonEditorIconGrid({
  value,
  onValueChange,
}: {
  value: string | undefined;
  onValueChange: (next: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {BUTTON_ICON_OPTIONS.map((option) => {
        const isSelected = value === option.value;

        return (
          <Button
            key={option.value}
            variant="outline"
            colorScheme="secondary"
            size="icon"
            className={cn(
              'h-10 w-10 border-input bg-background',
              isSelected && 'border-2 border-primary bg-primary/5 text-primary',
            )}
            onClick={() => onValueChange(option.value)}
            aria-label={option.label}
            aria-pressed={isSelected}
          >
            <Icon icon={option.icon} />
          </Button>
        );
      })}
    </div>
  );
}

const ICON_PLACEMENT_OPTIONS = [
  { value: 'before', label: 'Before Text' },
  { value: 'after', label: 'After Text' },
] as const;

export function ButtonEditorIconPlacement({
  value,
  onValueChange,
}: {
  value: string;
  onValueChange: (next: string) => void;
}) {
  const uid = useId();

  return (
    <div className="space-y-2">
      <Label>Icon Placement</Label>
      <RadioGroup value={value} onValueChange={onValueChange} className="grid grid-cols-2 gap-2">
        {ICON_PLACEMENT_OPTIONS.map((option) => {
          const optionId = `${uid}-icon-placement-${option.value}`;
          const isSelected = value === option.value;

          return (
            <Label key={option.value} htmlFor={optionId} className="cursor-pointer">
              <div
                className={cn(
                  'relative flex flex-col items-center gap-2 rounded-md border border-input bg-background p-3 transition-colors',
                  isSelected && 'border-primary bg-primary/5',
                )}
              >
                <RadioGroupItem id={optionId} value={option.value} className="sr-only" />
                <div
                  className={cn(
                    buttonVariants({
                      variant: 'solid',
                      colorScheme: 'primary',
                      size: 'sm',
                    }),
                    'pointer-events-none min-w-28',
                  )}
                >
                  <span className="inline-flex w-full items-center gap-2">
                    {option.value === 'before' ? (
                      <Icon icon={faSparkles} className="size-4 shrink-0" />
                    ) : null}
                    <span className="flex-1 text-center">Button</span>
                    {option.value === 'after' ? (
                      <Icon icon={faSparkles} className="size-4 shrink-0" />
                    ) : null}
                  </span>
                </div>
                <span className="text-xs font-medium text-foreground">{option.label}</span>
                {isSelected ? (
                  <span className="absolute right-2 bottom-2 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Icon icon={faCheck} className="size-3 text-primary-foreground" />
                  </span>
                ) : null}
              </div>
            </Label>
          );
        })}
      </RadioGroup>
    </div>
  );
}

export function ButtonEditorLinkToggles({
  openInNewTab,
  nofollow,
  onOpenInNewTabChange,
  onNofollowChange,
  showOpenInNewTab = true,
  showNofollow = true,
}: {
  openInNewTab: boolean;
  nofollow: boolean;
  onOpenInNewTabChange: (checked: boolean) => void;
  onNofollowChange: (checked: boolean) => void;
  showOpenInNewTab?: boolean;
  showNofollow?: boolean;
}) {
  return (
    <>
      {showOpenInNewTab ? (
        <ButtonEditorSettingToggle
          title="Open link in new tab"
          checked={openInNewTab}
          onCheckedChange={onOpenInNewTabChange}
        />
      ) : null}
      {showNofollow ? (
        <ButtonEditorSettingToggle
          title="Make link nofollow"
          description="Search engines will ignore nofollow links"
          checked={nofollow}
          onCheckedChange={onNofollowChange}
        />
      ) : null}
    </>
  );
}
